import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowRight, ArrowLeft, Code2, Smartphone, Cpu, Layers, BarChart3, Cloud, Sparkles, ChevronRight } from 'lucide-react';
import { servicesData } from '../../data/servicesData';

interface ServicesSliderProps {
  onSelectService: (slug: string) => void;
}

const accentConfig: Record<string, { bg: string; border: string; text: string; glow: string; badge: string }> = {
  purple: {
    bg: 'bg-[#7B2EFF]',
    border: 'border-[#7B2EFF]',
    text: 'text-[#7B2EFF]',
    glow: 'shadow-[0_0_30px_rgba(123,46,255,0.25)]',
    badge: 'bg-[#7B2EFF]/10 border-[#7B2EFF]/30 text-[#7B2EFF]',
  },
  blue: {
    bg: 'bg-[#159DE8]',
    border: 'border-[#159DE8]',
    text: 'text-[#159DE8]',
    glow: 'shadow-[0_0_30px_rgba(21,157,232,0.25)]',
    badge: 'bg-[#159DE8]/10 border-[#159DE8]/30 text-[#159DE8]',
  },
  orange: {
    bg: 'bg-[#FF851B]',
    border: 'border-[#FF851B]',
    text: 'text-[#FF851B]',
    glow: 'shadow-[0_0_30px_rgba(255,133,27,0.25)]',
    badge: 'bg-[#FF851B]/10 border-[#FF851B]/30 text-[#FF851B]',
  },
  green: {
    bg: 'bg-[#00C853]',
    border: 'border-[#00C853]',
    text: 'text-[#00C853]',
    glow: 'shadow-[0_0_30px_rgba(0,200,83,0.25)]',
    badge: 'bg-[#00C853]/10 border-[#00C853]/30 text-[#00C853]',
  },
  coral: {
    bg: 'bg-[#FF5A36]',
    border: 'border-[#FF5A36]',
    text: 'text-[#FF5A36]',
    glow: 'shadow-[0_0_30px_rgba(255,90,54,0.25)]',
    badge: 'bg-[#FF5A36]/10 border-[#FF5A36]/30 text-[#FF5A36]',
  },
};

const getIcon = (name: string) => {
  const cls = 'w-7 h-7 text-white';
  switch (name) {
    case 'Code2': return <Code2 className={cls} />;
    case 'Smartphone': return <Smartphone className={cls} />;
    case 'Cpu': return <Cpu className={cls} />;
    case 'Layers': return <Layers className={cls} />;
    case 'Cloud': return <Cloud className={cls} />;
    case 'BarChart3':
    default: return <BarChart3 className={cls} />;
  }
};

export const ServicesSlider: React.FC<ServicesSliderProps> = ({ onSelectService }) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState<'left' | 'right'>('right');
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const total = servicesData.length;

  const goTo = useCallback((idx: number, dir: 'left' | 'right') => {
    if (animating) return;
    setAnimating(true);
    setDirection(dir);
    setTimeout(() => {
      setActiveIdx(idx);
      setAnimating(false);
    }, 380);
  }, [animating]);

  const next = useCallback(() => {
    goTo((activeIdx + 1) % total, 'right');
  }, [activeIdx, total, goTo]);

  const prev = useCallback(() => {
    goTo((activeIdx - 1 + total) % total, 'left');
  }, [activeIdx, total, goTo]);

  // Auto-advance every 4 seconds
  useEffect(() => {
    if (paused) return;
    timerRef.current = setInterval(next, 4000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [next, paused]);

  const service = servicesData[activeIdx];
  const accent = accentConfig[service.accentColor] ?? accentConfig.blue;

  // Slide animation classes
  const slideClass = animating
    ? direction === 'right'
      ? 'opacity-0 translate-x-8'
      : 'opacity-0 -translate-x-8'
    : 'opacity-100 translate-x-0';

  return (
    <section className="bg-[#071426] py-20 border-b border-[#1b2f4a] relative overflow-hidden">
      {/* Ambient background glow tied to active service accent */}
      <div
        className="absolute inset-0 pointer-events-none transition-all duration-700"
        style={{ background: 'radial-gradient(ellipse 60% 50% at 70% 50%, rgba(21,157,232,0.06) 0%, transparent 70%)' }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14 space-y-3">
          <div className="inline-flex items-center gap-2">
            <span className="h-px w-8 bg-[#FF851B]" />
            <span className="text-xs font-bold uppercase tracking-widest text-[#FF851B] flex items-center gap-1.5">
              <Sparkles size={12} />
              OUR SERVICES
            </span>
            <span className="h-px w-8 bg-[#FF851B]" />
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white font-display tracking-tight">
            Comprehensive Digital Solutions
          </h2>
          <p className="text-[#D5DFEC] text-sm sm:text-base leading-relaxed">
            Explore our full suite of technology services — from AI-powered development to growth marketing.
          </p>
        </div>

        {/* Main Slider */}
        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">

            {/* Left Panel — Service Detail */}
            <div className="lg:col-span-7 xl:col-span-8">
              <div
                className={`h-full p-8 sm:p-10 rounded-3xl bg-[#0c1a2d] border ${accent.border}/40 transition-all duration-500 ${accent.glow} relative overflow-hidden`}
              >
                {/* Card ambient glow */}
                <div className={`absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl pointer-events-none ${accent.bg} opacity-5`} />

                <div
                  className={`relative z-10 flex flex-col h-full transition-all duration-[380ms] ease-out ${slideClass}`}
                >
                  {/* Top row: icon + badge */}
                  <div className="flex items-start justify-between mb-6">
                    <div className={`w-16 h-16 rounded-2xl ${accent.bg} flex items-center justify-center shadow-lg`}>
                      {getIcon(service.iconName)}
                    </div>
                    <div className={`px-3 py-1 rounded-full border text-xs font-mono font-bold ${accent.badge}`}>
                      {String(activeIdx + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
                    </div>
                  </div>

                  {/* Service tag */}
                  <span className={`text-xs font-mono font-bold uppercase tracking-widest mb-2 ${accent.text}`}>
                    Enterprise Technology Service
                  </span>

                  {/* Title */}
                  <h3 className="text-2xl sm:text-3xl font-extrabold text-white font-display mb-4 leading-tight">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-sm sm:text-base text-[#D5DFEC] leading-relaxed mb-6 flex-1">
                    {service.fullDescription || service.shortDescription}
                  </p>

                  {/* Solution bullets (first 4 solutions) */}
                  {service.solutions && service.solutions.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-8">
                      {service.solutions.slice(0, 4).map((sol, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs text-[#D5DFEC]">
                          <ChevronRight size={13} className={`${accent.text} shrink-0 mt-0.5`} />
                          <span>{sol}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* CTA */}
                  <button
                    onClick={() => onSelectService(service.slug)}
                    className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm text-white ${accent.bg} hover:opacity-90 transition-all duration-200 shadow-md hover:shadow-lg hover:-translate-y-0.5 self-start`}
                  >
                    <span>Explore {service.title}</span>
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Right Panel — Service List Nav */}
            <div className="lg:col-span-5 xl:col-span-4 flex flex-col gap-2">
              {servicesData.map((s, idx) => {
                const a = accentConfig[s.accentColor] ?? accentConfig.blue;
                const isActive = idx === activeIdx;
                return (
                  <button
                    key={s.id}
                    onClick={() => goTo(idx, idx > activeIdx ? 'right' : 'left')}
                    className={`w-full text-left p-4 rounded-2xl border transition-all duration-300 flex items-center gap-3 group ${
                      isActive
                        ? `${a.bg} border-transparent text-white shadow-lg scale-[1.02]`
                        : 'bg-[#0c1a2d] border-[#1b2f4a] text-[#D5DFEC] hover:border-[#1b2f4a]/80 hover:bg-[#0d1f35]'
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300 ${
                      isActive ? 'bg-white/20' : `${a.bg} opacity-70 group-hover:opacity-100`
                    }`}>
                      {getIcon(s.iconName)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className={`block text-sm font-bold truncate ${isActive ? 'text-white' : 'text-white'}`}>
                        {s.title}
                      </span>
                      <span className={`block text-[11px] truncate ${isActive ? 'text-white/80' : 'text-[#8FA0B8]'}`}>
                        {s.shortDescription.substring(0, 48)}…
                      </span>
                    </div>
                    {isActive && <ChevronRight size={16} className="shrink-0 text-white" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Prev / Next Controls */}
          <div className="flex items-center justify-between mt-8">
            {/* Progress dots */}
            <div className="flex items-center gap-1.5">
              {servicesData.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goTo(idx, idx > activeIdx ? 'right' : 'left')}
                  aria-label={`Go to service ${idx + 1}`}
                  className={`rounded-full transition-all duration-300 ${
                    idx === activeIdx
                      ? `w-6 h-2 ${accent.bg}`
                      : 'w-2 h-2 bg-[#1b2f4a] hover:bg-[#8FA0B8]'
                  }`}
                />
              ))}
            </div>

            {/* Arrow buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={prev}
                aria-label="Previous service"
                className="w-10 h-10 rounded-xl bg-[#0c1a2d] border border-[#1b2f4a] flex items-center justify-center text-white hover:border-[#FF851B] hover:text-[#FF851B] transition-all duration-200"
              >
                <ArrowLeft size={18} />
              </button>
              <button
                onClick={next}
                aria-label="Next service"
                className="w-10 h-10 rounded-xl bg-[#0c1a2d] border border-[#1b2f4a] flex items-center justify-center text-white hover:border-[#FF851B] hover:text-[#FF851B] transition-all duration-200"
              >
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};
